import { Box, Container, Typography, Button, Grid } from "@mui/material";
import { Link } from "react-router-dom";
import { useDocumentTitle } from "../../utils/data";
import { keyframes } from "@mui/system";
// import AboutImg1 from "../../assets/images/about1.avif";
// import AboutImg2 from "../../assets/images/about2.avif";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideInLeft = keyframes`
  from { opacity: 0; transform: translateX(-40px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideInRight = keyframes`
  from { opacity: 0; transform: translateX(40px); }
  to { opacity: 1; transform: translateX(0); }
`;

const scaleInCenter = keyframes`
  from { opacity: 0; transform: scale(0.85); }
  to { opacity: 1; transform: scale(1); }
`;

const floatBall = keyframes`
  0%, 100% { transform: translate(0, 0); }
  25% { transform: translate(20px, -20px); }
  50% { transform: translate(-10px, 20px); }
  75% { transform: translate(10px, -10px); }
`;

const bounce = keyframes`
  0%, 100% { transform: scale(1); }
  25% { transform: scale(1.2) rotate(-5deg); }
  50% { transform: scale(1.1) rotate(5deg); }
  75% { transform: scale(1.15) rotate(-3deg); }
`;


const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(0.95); }
`;

const expandWidth = keyframes`
  from { width: 0%; }
  to { width: 100%; }
`;

const bubbleFloat = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const specializedData = [
  { icon: "🤖", title: "AI & Agentic AI Solutions", desc: "Next-gen intelligent automation and AI-powered workflows that scale with your business" },
  { icon: "💼", title: "IT Consulting & Strategy", desc: "Technology roadmaps and digital strategies that accelerate growth and competitive edge" },
  { icon: "💻", title: "Custom Application Development", desc: "Bespoke software engineered precisely for your unique needs and objectives" },
  { icon: "🔍", title: "Quality Engineering & Assurance", desc: "Rigorous testing frameworks that ensure flawless, reliable delivery every time" },
  { icon: "📱", title: "Mobile Application Development", desc: "iOS & Android experiences users love, built with performance and design in mind" },
  { icon: "⚙️", title: "Managed IT Services", desc: "End-to-end IT management and support for peak operational performance" },
];

const shapingFutureData = [
  {
    icon: "🚀",
    title: "Expert-Led Innovation",
    description: "At Briozon, we combine industry expertise with innovative thinking to deliver transformative solutions that drive business success.",
  },
  {
    icon: "🎯",
    title: "Tailored Solutions for You",
    description: "We provide customized, scalable solutions designed to meet the unique challenges of your business and industry.",
  },
  {
    icon: "⭐",
    title: "Commitment to Excellence",
    description: "Our dedication to quality ensures that every project is executed with precision, efficiency, and a focus on long-term impact.",
  },
  {
    icon: "🤝",
    title: "Collaboration for Success",
    description: "We believe in building strong, collaborative partnerships with our clients to foster growth and achieve shared goals.",
  },
];

const About = () => {
  useDocumentTitle("Briozon - About Us");

  return (
    <Box>
      {/* Hero Banner */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #f8fafc 0%, #f0f4ff 100%)",
          py: { xs: 6, sm: 8, md: 10 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Floating decorative elements */}
        <Box
          sx={{
            position: "absolute",
            top: "10%",
            right: "5%",
            width: 120,
            height: 120,
            borderRadius: "50%",
            background: "linear-gradient(135deg, rgba(91,124,250,0.1) 0%, rgba(118,75,162,0.1) 100%)",
            animation: `${floatBall} 8s ease-in-out infinite`,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "15%",
            left: "3%",
            width: 80,
            height: 80,
            borderRadius: "50%",
            background: "linear-gradient(135deg, rgba(118,75,162,0.1) 0%, rgba(91,124,250,0.1) 100%)",
            animation: `${floatBall} 6s ease-in-out infinite 1s`,
          }}
        />

        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, sm: 6, md: 8 }} alignItems="center">
            {/* Left Content */}
            <Grid item xs={12} md={6}>
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    fontSize: { xs: "12px", sm: "14px" },
                    fontWeight: 700,
                    color: "#5b7cfa",
                    letterSpacing: "2px",
                    mb: 1,
                    display: "block",
                    animation: `${fadeUp} 0.6s ease-out 0.05s both`,
                  }}
                >
                  ABOUT US
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: "32px", sm: "42px", md: "48px" },
                    fontWeight: 800,
                    color: "#1b1f33",
                    lineHeight: 1.2,
                    mb: 2,
                    animation: `${fadeUp} 0.6s ease-out 0.1s both`,
                  }}
                >
                  Innovative Technology Solutions for{" "}
                  <Box
                    component="span"
                    sx={{
                      background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }}
                  >
                    Business Growth
                  </Box>
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { xs: "15px", sm: "17px" },
                    color: "#4c5472",
                    lineHeight: 1.8,
                    mb: 3,
                    animation: `${fadeUp} 0.6s ease-out 0.2s both`,
                  }}
                >
                  We provide innovative technology solutions that drive business growth and success. 
                  Our expert team delivers tailored strategies to ensure sustainable transformation 
                  and competitiveness.
                </Typography>
                <Button
                  component={Link}
                  to="/contact"
                  variant="contained"
                  sx={{
                    background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                    color: "#fff",
                    fontWeight: 700,
                    px: 4,
                    py: 1.5,
                    borderRadius: "12px",
                    fontSize: "16px",
                    textTransform: "none",
                    boxShadow: "0 8px 24px rgba(91, 124, 250, 0.3)",
                    animation: `${fadeUp} 0.6s ease-out 0.3s both`,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 12px 32px rgba(91, 124, 250, 0.4)",
                    },
                  }}
                >
                  Get in Touch
                </Button>
              </Box>
            </Grid>

            {/* Right - Creative Visual */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: "relative",
                  height: { xs: 320, sm: 400, md: 450 },
                  animation: `${scaleInCenter} 0.8s ease-out 0.2s both`,
                }}
              >
                {/* Main gradient circle */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 220, sm: 280, md: 320 },
                    height: { xs: 220, sm: 280, md: 320 },
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, rgba(91,124,250,0.15) 0%, rgba(118,75,162,0.15) 100%)",
                    filter: "blur(40px)",
                  }}
                />

                {/* Central icon */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 100, sm: 120 },
                    height: { xs: 100, sm: 120 },
                    borderRadius: "24px",
                    background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: { xs: "48px", sm: "56px" },
                    boxShadow: "0 20px 40px rgba(91, 124, 250, 0.4)",
                    animation: `${floatBall} 6s ease-in-out infinite`,
                    zIndex: 2,
                  }}
                >
                  🚀
                </Box>

                {/* Floating stat cards */}
                <Box
                  sx={{
                    position: "absolute",
                    top: { xs: "5%", sm: "8%" },
                    left: { xs: "5%", sm: "10%" },
                    p: 2,
                    background: "rgba(255,255,255,0.95)",
                    borderRadius: "16px",
                    boxShadow: "0 8px 32px rgba(17, 24, 39, 0.1)",
                    backdropFilter: "blur(10px)",
                    animation: `${floatBall} 5s ease-in-out infinite 0.5s`,
                    zIndex: 1,
                  }}
                >
                  <Typography sx={{ fontSize: { xs: "24px", sm: "32px" }, fontWeight: 800, color: "#5b7cfa", lineHeight: 1 }}>
                    10+
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: "#4b5563", fontWeight: 600 }}>
                    Years Experience
                  </Typography>
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    top: { xs: "5%", sm: "5%" },
                    right: { xs: "5%", sm: "8%" },
                    p: 2,
                    background: "rgba(255,255,255,0.95)",
                    borderRadius: "16px",
                    boxShadow: "0 8px 32px rgba(17, 24, 39, 0.1)",
                    backdropFilter: "blur(10px)",
                    animation: `${floatBall} 6s ease-in-out infinite 1s`,
                    zIndex: 1,
                  }}
                >
                  <Typography sx={{ fontSize: { xs: "24px", sm: "32px" }, fontWeight: 800, color: "#764ba2", lineHeight: 1 }}>
                    200+
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: "#4b5563", fontWeight: 600 }}>
                    Projects Delivered
                  </Typography>
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    bottom: { xs: "8%", sm: "10%" },
                    left: { xs: "8%", sm: "15%" },
                    p: 2,
                    background: "rgba(255,255,255,0.95)",
                    borderRadius: "16px",
                    boxShadow: "0 8px 32px rgba(17, 24, 39, 0.1)",
                    backdropFilter: "blur(10px)",
                    animation: `${floatBall} 7s ease-in-out infinite 1.5s`,
                    zIndex: 1,
                  }}
                >
                  <Typography sx={{ fontSize: { xs: "24px", sm: "32px" }, fontWeight: 800, color: "#5b7cfa", lineHeight: 1 }}>
                    100%
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: "#4b5563", fontWeight: 600 }}>
                    Client Satisfaction
                  </Typography>
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    bottom: { xs: "5%", sm: "8%" },
                    right: { xs: "5%", sm: "10%" },
                    p: 2,
                    background: "rgba(255,255,255,0.95)",
                    borderRadius: "16px",
                    boxShadow: "0 8px 32px rgba(17, 24, 39, 0.1)",
                    backdropFilter: "blur(10px)",
                    animation: `${floatBall} 5.5s ease-in-out infinite 2s`,
                    zIndex: 1,
                  }}
                >
                  <Typography sx={{ fontSize: { xs: "24px", sm: "32px" }, fontWeight: 800, color: "#764ba2", lineHeight: 1 }}>
                    50+
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: "#4b5563", fontWeight: 600 }}>
                    Expert Team
                  </Typography>
                </Box>

                {/* Floating service icons */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "38%",
                    left: { xs: "0%", sm: "5%" },
                    width: 50,
                    height: 50,
                    borderRadius: "14px",
                    background: "linear-gradient(135deg, rgba(91,124,250,0.15) 0%, rgba(118,75,162,0.15) 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "24px",
                    animation: `${floatBall} 4s ease-in-out infinite 0.8s`,
                    boxShadow: "0 4px 16px rgba(91, 124, 250, 0.2)",
                  }}
                >
                  🤖
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    top: "35%",
                    right: { xs: "0%", sm: "3%" },
                    width: 50,
                    height: 50,
                    borderRadius: "14px",
                    background: "linear-gradient(135deg, rgba(118,75,162,0.15) 0%, rgba(91,124,250,0.15) 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "24px",
                    animation: `${floatBall} 5s ease-in-out infinite 1.2s`,
                    boxShadow: "0 4px 16px rgba(118, 75, 162, 0.2)",
                  }}
                >
                  💻
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    top: "60%",
                    left: { xs: "15%", sm: "20%" },
                    width: 44,
                    height: 44,
                    borderRadius: "12px",
                    background: "linear-gradient(135deg, rgba(91,124,250,0.2) 0%, rgba(118,75,162,0.2) 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "20px",
                    animation: `${floatBall} 6s ease-in-out infinite 0.3s`,
                  }}
                >
                  📱
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    top: "62%",
                    right: { xs: "15%", sm: "18%" },
                    width: 44,
                    height: 44,
                    borderRadius: "12px",
                    background: "linear-gradient(135deg, rgba(118,75,162,0.2) 0%, rgba(91,124,250,0.2) 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "20px",
                    animation: `${floatBall} 4.5s ease-in-out infinite 1.8s`,
                  }}
                >
                  ⚙️
                </Box>

                {/* Decorative rings */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 200, sm: 260 },
                    height: { xs: 200, sm: 260 },
                    borderRadius: "50%",
                    border: "2px dashed rgba(91, 124, 250, 0.2)",
                    animation: `${floatBall} 10s ease-in-out infinite`,
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 280, sm: 360 },
                    height: { xs: 280, sm: 360 },
                    borderRadius: "50%",
                    border: "1px dashed rgba(118, 75, 162, 0.15)",
                    animation: `${floatBall} 12s ease-in-out infinite reverse`,
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ══════════════════════════════════════════════════════ */}
      {/* WHO WE ARE */}
      {/* ══════════════════════════════════════════════════════ */}
      <Box sx={{
        py: { xs: 8, sm: 10, md: 14 },
        background: "linear-gradient(160deg, #eff6ff 0%, #ffffff 50%, #f0f9ff 100%)",
        position: "relative", overflow: "hidden",
      }}>
        <Box sx={{ position: "absolute", inset: 0, zIndex: 0, backgroundImage: "radial-gradient(circle, rgba(59,130,246,0.07) 1px, transparent 1px)", backgroundSize: "38px 38px" }} />
        <Box sx={{ position: "absolute", top: "-10%", right: "5%", width: 420, height: 420, borderRadius: "50%", background: "radial-gradient(circle, rgba(59,130,246,0.08) 0%, transparent 70%)", zIndex: 0 }} />
        <Box sx={{ position: "absolute", bottom: "-10%", left: "0%", width: 360, height: 360, borderRadius: "50%", background: "radial-gradient(circle, rgba(56,189,248,0.06) 0%, transparent 70%)", zIndex: 0 }} />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Grid container spacing={{ xs: 4, sm: 6, md: 8 }} alignItems="center">

            {/* Left — Company DNA Card */}
            <Grid item xs={12} md={6} sx={{ display: "flex", flexDirection: "column" }}>
              <Box sx={{ position: "relative", animation: `${slideInLeft} 0.8s ease-out both` }}>
                {/* Gradient border wrapper */}
                <Box sx={{
                  background: "linear-gradient(135deg, #93c5fd 0%, #c4b5fd 100%)",
                  borderRadius: "28px", p: "2px",
                  boxShadow: "0 24px 64px rgba(139,92,246,0.18)",
                }}>
                  <Box sx={{
                    background: "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
                    borderRadius: "26px", p: { xs: 3, sm: 4 },
                    position: "relative", overflow: "hidden",
                  }}>
                    {/* Dot grid */}
                    <Box sx={{
                      position: "absolute", inset: 0, zIndex: 0, borderRadius: "26px",
                      backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.18) 1px, transparent 1px)",
                      backgroundSize: "28px 28px",
                    }} />
                    {/* Card header */}
                    <Box sx={{ position: "relative", zIndex: 1, mb: 3 }}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                        <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "rgba(255,255,255,0.7)", letterSpacing: "2px" }}>🏢 COMPANY OVERVIEW</Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, px: 1.5, py: 0.5, borderRadius: "100px", background: "rgba(34,197,94,0.15)", border: "1px solid rgba(34,197,94,0.3)" }}>
                          <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: "#22c55e", animation: `${pulse} 1.5s ease-in-out infinite` }} />
                          <Typography sx={{ fontSize: "11px", color: "#4ade80", fontWeight: 700 }}>ACTIVE</Typography>
                        </Box>
                      </Box>
                      <Typography sx={{ fontSize: { xs: "22px", sm: "26px" }, fontWeight: 800, color: "#fff", lineHeight: 1.3 }}>Briozon Technology</Typography>
                      <Typography sx={{ fontSize: "13px", color: "rgba(255,255,255,0.7)", mt: 0.5 }}>Est. 2017 · Global IT Solutions</Typography>
                    </Box>

                    {/* 2×2 stat grid */}
                    <Grid container spacing={1.5} sx={{ position: "relative", zIndex: 1, mb: 3 }}>
                      {[
                        { val: "10+", label: "Years of Excellence", icon: "📅", color: "#60a5fa" },
                        { val: "200+", label: "Projects Shipped", icon: "🚀", color: "#a78bfa" },
                        { val: "100%", label: "Client Satisfaction", icon: "⭐", color: "#38bdf8" },
                        { val: "50+", label: "Expert Engineers", icon: "👨‍💻", color: "#c084fc" },
                      ].map((s, si) => (
                        <Grid item xs={6} key={s.label}>
                          <Box sx={{
                            p: 1.75,
                            background: "rgba(255,255,255,0.15)", borderRadius: "14px",
                            border: "1px solid rgba(255,255,255,0.25)",
                            transition: "all 0.3s ease",
                            animation: `${fadeUp} 0.5s ease-out ${0.1 + si * 0.08}s both`,
                            "&:hover": {
                              background: "rgba(255,255,255,0.14)",
                              border: `1px solid ${s.color}60`,
                              transform: "translateY(-4px) scale(1.03)",
                              boxShadow: `0 8px 24px ${s.color}30`,
                            },
                          }}>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                              <Typography sx={{ fontSize: "20px" }}>{s.icon}</Typography>
                              <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: s.color, animation: `${pulse} ${1.5 + si * 0.3}s ease-in-out infinite` }} />
                            </Box>
                            <Typography sx={{ fontSize: { xs: "22px", sm: "26px" }, fontWeight: 800, color: "#fff", lineHeight: 1, background: `linear-gradient(135deg, #fff 0%, ${s.color} 100%)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>{s.val}</Typography>
                            <Typography sx={{ fontSize: "11px", color: "rgba(255,255,255,0.8)", fontWeight: 600, mt: 0.25 }}>{s.label}</Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>

                    {/* Progress bars */}
                    <Box sx={{ position: "relative", zIndex: 1 }}>
                      {[
                        { label: "Client Retention Rate", pct: 98, color: "#60a5fa" },
                        { label: "On-Time Delivery", pct: 95, color: "#a78bfa" },
                        { label: "Fortune 500 Clients", pct: 85, color: "#c084fc" },
                      ].map((b) => (
                        <Box key={b.label} sx={{ mb: 1.5 }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                            <Typography sx={{ fontSize: "12px", color: "rgba(255,255,255,0.8)", fontWeight: 600 }}>{b.label}</Typography>
                            <Typography sx={{ fontSize: "12px", color: b.color, fontWeight: 700 }}>{b.pct}%</Typography>
                          </Box>
                          <Box sx={{ height: 5, borderRadius: "100px", background: "rgba(255,255,255,0.07)", overflow: "hidden" }}>
                            <Box sx={{ height: "100%", width: `${b.pct}%`, borderRadius: "100px", background: `linear-gradient(90deg, ${b.color}, rgba(255,255,255,0.35))` }} />
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>

                {/* Floating badge */}
                <Box sx={{
                  position: "absolute", bottom: -18, right: { xs: 16, sm: 28 },
                  px: 2.5, py: 1, borderRadius: "12px",
                  background: "linear-gradient(135deg, #60a5fa 0%, #a78bfa 100%)",
                  boxShadow: "0 8px 24px rgba(139,92,246,0.35)",
                  animation: `${bubbleFloat} 3s ease-in-out infinite`,
                }}>
                  <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#fff", whiteSpace: "nowrap" }}>🏆 Fortune 500 Partner</Typography>
                </Box>
              </Box>
            </Grid>

            {/* Right — Text */}
            <Grid item xs={12} md={6}>
              <Box sx={{ animation: `${slideInRight} 0.8s ease-out both`, mt: { xs: 4, md: 0 } }}>
                {/* Chip */}
                <Box sx={{
                  display: "inline-flex", alignItems: "center", gap: 1,
                  px: 2, py: 0.75, borderRadius: "100px",
                  background: "linear-gradient(135deg, rgba(59,130,246,0.1) 0%, rgba(56,189,248,0.1) 100%)",
                  border: "1px solid rgba(59,130,246,0.25)",
                  mb: 2.5,
                }}>
                  <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: "#3b82f6", animation: `${pulse} 1.5s ease-in-out infinite` }} />
                  <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#1d4ed8", letterSpacing: "2px" }}>WHO WE ARE</Typography>
                </Box>

                <Typography variant="h3" sx={{ fontSize: { xs: "28px", sm: "36px", md: "42px" }, fontWeight: 800, color: "#1b1f33", mb: 2.5, lineHeight: 1.2 }}>
                  We Deliver Results.{" "}
                  <Box component="span" sx={{
                    background: "linear-gradient(135deg, #3b82f6 0%, #38bdf8 100%)",
                    WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text",
                    position: "relative",
                    "&::after": {
                      content: '""', position: "absolute", bottom: -4, left: 0,
                      height: "3px", borderRadius: "2px",
                      background: "linear-gradient(90deg, #3b82f6, #38bdf8)",
                      animation: `${expandWidth} 1.2s ease-out 0.6s both`,
                    },
                  }}>
                    We Make Things Happen.
                  </Box>
                </Typography>

                <Typography sx={{ fontSize: { xs: "16px", sm: "18px" }, color: "#4b5563", lineHeight: 1.85, mb: 2.5, textAlign: "justify" }}>
                  Briozon is an AI‑driven technology company built by experts in software engineering and digital transformation. We design intelligent, scalable solutions tailored to each client’s unique challenges.
                </Typography>
                <Typography sx={{ fontSize: { xs: "16px", sm: "18px" }, color: "#4b5563", lineHeight: 1.85, textAlign: "justify" }}>
                  With strong expertise in IT staffing, we also empower organizations with exceptional talent — because the right people are the engine behind every breakthrough.
                </Typography>

                {/* Value tags */}
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 3 }}>
                  {["AI Excellence","Innovation First", "Talent-Driven", "Client-Centric","Machine Intelligence", "Fortune 500 Ready"].map((tag) => (
                    <Box key={tag} sx={{
                      px: 2, py: 0.75, borderRadius: "100px",
                      background: "linear-gradient(135deg, rgba(59,130,246,0.08) 0%, rgba(56,189,248,0.08) 100%)",
                      border: "1px solid rgba(59,130,246,0.2)",
                    }}>
                      <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#1d4ed8" }}>✦ {tag}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ══════════════════════════════════════════════════════ */}
      {/* WHY BRIOZON */}
      {/* ══════════════════════════════════════════════════════ */}
      <Box sx={{
        py: { xs: 8, sm: 10, md: 14 },
        background: "linear-gradient(160deg, #eff6ff 0%, #f0f9ff 100%)",
        position: "relative", overflow: "hidden",
      }}>
        <Box sx={{ position: "absolute", inset: 0, zIndex: 0, backgroundImage: "radial-gradient(circle, rgba(59,130,246,0.07) 1px, transparent 1px)", backgroundSize: "38px 38px" }} />
        <Box sx={{ position: "absolute", top: "-10%", left: "20%", width: 420, height: 420, borderRadius: "50%", background: "radial-gradient(circle, rgba(59,130,246,0.08) 0%, transparent 70%)", zIndex: 0 }} />
        <Box sx={{ position: "absolute", bottom: "-10%", right: "10%", width: 360, height: 360, borderRadius: "50%", background: "radial-gradient(circle, rgba(56,189,248,0.07) 0%, transparent 70%)", zIndex: 0 }} />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{ textAlign: "center", mb: { xs: 6, sm: 7 } }}>
            <Box sx={{
              display: "inline-flex", alignItems: "center", gap: 1,
              px: 2, py: 0.75, borderRadius: "100px",
              background: "linear-gradient(135deg, rgba(59,130,246,0.1) 0%, rgba(56,189,248,0.1) 100%)",
              border: "1px solid rgba(59,130,246,0.25)",
              mb: 2.5,
            }}>
              <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: "#3b82f6", animation: `${pulse} 1.5s ease-in-out infinite` }} />
              <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#1d4ed8", letterSpacing: "2px" }}>WHY BRIOZON</Typography>
            </Box>
            <Typography variant="h3" sx={{ fontSize: { xs: "28px", sm: "36px", md: "44px" }, fontWeight: 800, color: "#1b1f33", lineHeight: 1.2 }}>
              Empowering Innovation through{" "}
              <Box component="span" sx={{ background: "linear-gradient(135deg, #3b82f6 0%, #38bdf8 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                Passion, Purpose & Technology
              </Box>
            </Typography>
          </Box>

          <Grid container spacing={{ xs: 2.5, sm: 3 }}>
            {[
              { icon: "🤝", title: "True Partnership", description: "We view our clients as true partners in the journey of digital transformation", color: "#3b82f6" },
              { icon: "🎯", title: "Long-term Relationships", description: "Our commitment to fostering strong relationships built on trust and mutual success", color: "#38bdf8" },
              { icon: "🔄", title: "Digital Transformation", description: "Helping clients achieve their goals and reshape the way business is conducted", color: "#3b82f6" },
              { icon: "👥", title: "Collaborative Environment", description: "Diverse team working together with a shared vision of 'Partnership through Enablement'", color: "#38bdf8" },
              { icon: "⭐", title: "Client Satisfaction", description: "Seamless collaborative experience providing exceptional value at every stage", color: "#3b82f6" },
              { icon: "🏆", title: "Proven Track Record", description: "Delivering for Fortune 500 companies with 100% customer satisfaction", color: "#38bdf8" },
            ].map((item, idx) => (
              <Grid item xs={12} sm={6} md={4} key={item.title} sx={{ display: "flex", flexDirection: "column" }}>
                <Box sx={{
                  flex: 1, p: { xs: 3, sm: 3.5 },
                  background: "#fff",
                  borderRadius: "20px", border: "1px solid rgba(59,130,246,0.12)",
                  boxShadow: "0 4px 16px rgba(17,24,39,0.05)",
                  position: "relative", overflow: "hidden",
                  cursor: "pointer", zIndex: 0,
                  transition: "all 0.35s cubic-bezier(0.34,1.56,0.64,1)",
                  animation: `${fadeUp} 0.5s ease-out ${0.08 * idx}s both`,
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: "0 20px 48px rgba(59,130,246,0.15)",
                    border: "1px solid rgba(59,130,246,0.3)",
                    zIndex: 2,
                    "& .why-icon": { animation: `${bounce} 0.6s ease-in-out`, filter: `drop-shadow(0 4px 12px ${item.color}80)` },
                  },
                }}>
                  {/* Ghost number */}
                  <Typography sx={{ position: "absolute", top: 6, right: 14, fontSize: "72px", fontWeight: 900, lineHeight: 1, color: `${item.color}12`, userSelect: "none" }}>
                    {String(idx + 1).padStart(2, "0")}
                  </Typography>
                  {/* Left accent bar */}
                  <Box sx={{ position: "absolute", top: 0, left: 0, width: "4px", height: "100%", background: `linear-gradient(180deg, ${item.color} 0%, ${item.color}80 100%)`, borderRadius: "4px 0 0 4px" }} />
                  <Box className="why-icon" sx={{ width: 56, height: 56, borderRadius: "16px", background: `${item.color}12`, border: `1px solid ${item.color}25`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "28px", mb: 2.5, transition: "all 0.3s ease" }}>
                    {item.icon}
                  </Box>
                  <Typography sx={{ fontSize: { xs: "18px", sm: "20px" }, fontWeight: 700, color: "#1f2937", mb: 1 }}>{item.title}</Typography>
                  <Typography sx={{ fontSize: { xs: "14px", sm: "15px" }, color: "#6b7280", lineHeight: 1.7 }}>{item.description}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ══════════════════════════════════════════════════════ */}
      {/* WE ARE SPECIALIZED IN */}
      {/* ══════════════════════════════════════════════════════ */}
      <Box sx={{ py: { xs: 8, sm: 10, md: 14 }, background: "#fff", position: "relative", overflow: "hidden" }}>
        <Box sx={{ position: "absolute", top: "-8%", right: "-5%", width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(circle, rgba(59,130,246,0.06) 0%, transparent 70%)", zIndex: 0 }} />
        <Box sx={{ position: "absolute", bottom: "-8%", left: "-5%", width: 350, height: 350, borderRadius: "50%", background: "radial-gradient(circle, rgba(56,189,248,0.06) 0%, transparent 70%)", zIndex: 0 }} />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{ textAlign: "center", mb: { xs: 6, sm: 7 } }}>
            <Box sx={{
              display: "inline-flex", alignItems: "center", gap: 1,
              px: 2, py: 0.75, borderRadius: "100px",
              background: "linear-gradient(135deg, rgba(59,130,246,0.1) 0%, rgba(56,189,248,0.1) 100%)",
              border: "1px solid rgba(59,130,246,0.25)",
              mb: 2.5,
            }}>
              <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: "#3b82f6", animation: `${pulse} 1.5s ease-in-out infinite` }} />
              <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#1d4ed8", letterSpacing: "2px" }}>OUR EXPERTISE</Typography>
            </Box>
            <Typography variant="h3" sx={{ fontSize: { xs: "28px", sm: "36px", md: "44px" }, fontWeight: 800, color: "#1b1f33" }}>
              We Are{" "}
              <Box component="span" sx={{ background: "linear-gradient(135deg, #3b82f6 0%, #38bdf8 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                Specialized In
              </Box>
            </Typography>
          </Box>

          <Grid container spacing={{ xs: 2.5, sm: 3 }}>
            {specializedData.map((item, idx) => (
              <Grid item xs={12} sm={6} md={4} key={item.title} sx={{ display: "flex", flexDirection: "column" }}>
                <Box sx={{
                  flex: 1, display: "flex", alignItems: "flex-start", gap: 2,
                  p: { xs: 2.5, sm: 3 },
                  background: "#fff", borderRadius: "20px",
                  border: "1px solid rgba(59,130,246,0.12)",
                  boxShadow: "0 4px 16px rgba(17,24,39,0.05)",
                  position: "relative", overflow: "hidden",
                  cursor: "pointer", zIndex: 0,
                  transition: "all 0.35s ease",
                  animation: `${fadeUp} 0.5s ease-out ${0.08 * idx}s both`,
                  "&:hover": {
                    transform: "translateY(-6px)",
                    boxShadow: "0 20px 48px rgba(59,130,246,0.14)",
                    border: "1px solid rgba(59,130,246,0.28)",
                    zIndex: 2,
                    "& .spec-sweep": { width: "100%" },
                    "& .spec-icon": { animation: `${bounce} 0.6s ease-in-out`, background: "linear-gradient(135deg, #3b82f6 0%, #38bdf8 100%)" },
                    "& .spec-title": { background: "linear-gradient(135deg, #3b82f6 0%, #38bdf8 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" },
                  },
                }}>
                  {/* Left accent bar */}
                  <Box sx={{ position: "absolute", top: 0, left: 0, width: "4px", height: "100%", background: "linear-gradient(180deg, #3b82f6 0%, #38bdf8 100%)", borderRadius: "4px 0 0 4px" }} />
                  {/* Bottom sweep */}
                  <Box className="spec-sweep" sx={{ position: "absolute", bottom: 0, left: 0, height: "2px", width: "28%", background: "linear-gradient(90deg, #3b82f6, #38bdf8)", transition: "width 0.4s ease" }} />

                  <Box className="spec-icon" sx={{ width: 60, height: 60, borderRadius: "16px", background: "linear-gradient(135deg, rgba(59,130,246,0.1) 0%, rgba(56,189,248,0.1) 100%)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "30px", flexShrink: 0, transition: "all 0.3s ease" }}>
                    {item.icon}
                  </Box>
                  <Box>
                    <Typography className="spec-title" sx={{ fontSize: { xs: "16px", sm: "18px" }, fontWeight: 700, color: "#1f2937", mb: 0.75, transition: "all 0.3s ease" }}>
                      {item.title}
                    </Typography>
                    <Typography sx={{ fontSize: "14px", color: "#6b7280", lineHeight: 1.65 }}>{item.desc}</Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ══════════════════════════════════════════════════════ */}
      {/* SHAPING YOUR FUTURE */}
      {/* ══════════════════════════════════════════════════════ */}
      <Box sx={{
        py: { xs: 8, sm: 10, md: 14 },
        background: "linear-gradient(160deg, #eff6ff 0%, #ffffff 50%, #f0f9ff 100%)",
        position: "relative", overflow: "hidden",
      }}>
        <Box sx={{ position: "absolute", inset: 0, zIndex: 0, backgroundImage: "radial-gradient(circle, rgba(59,130,246,0.07) 1px, transparent 1px)", backgroundSize: "38px 38px" }} />
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{ textAlign: "center", mb: { xs: 6, sm: 7 } }}>
            <Box sx={{
              display: "inline-flex", alignItems: "center", gap: 1,
              px: 2, py: 0.75, borderRadius: "100px",
              background: "linear-gradient(135deg, rgba(59,130,246,0.1) 0%, rgba(56,189,248,0.1) 100%)",
              border: "1px solid rgba(59,130,246,0.25)",
              mb: 2.5,
            }}>
              <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: "#3b82f6", animation: `${pulse} 1.5s ease-in-out infinite` }} />
              <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#1d4ed8", letterSpacing: "2px" }}>OUR APPROACH</Typography>
            </Box>
            <Typography variant="h3" sx={{ fontSize: { xs: "28px", sm: "36px", md: "44px" }, fontWeight: 800, color: "#1b1f33", lineHeight: 1.2 }}>
              Shaping Your Future:{" "}
              <Box component="span" sx={{ background: "linear-gradient(135deg, #3b82f6 0%, #38bdf8 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                A Trusted Partner in Innovation
              </Box>
            </Typography>
          </Box>

          <Grid container spacing={{ xs: 2.5, sm: 3 }}>
            {shapingFutureData.map((item, idx) => {
              const stepColors = ["#3b82f6", "#38bdf8", "#0ea5e9", "#60a5fa"];
              const color = stepColors[idx];
              return (
                <Grid item xs={12} sm={6} md={3} key={item.title} sx={{ display: "flex", flexDirection: "column" }}>
                  <Box sx={{
                    flex: 1, p: { xs: 3, sm: 3.5 },
                    background: "#fff",
                    borderRadius: "20px", border: "1px solid rgba(59,130,246,0.12)",
                    boxShadow: "0 4px 16px rgba(17,24,39,0.05)",
                    display: "flex", flexDirection: "column",
                    position: "relative", overflow: "hidden",
                    cursor: "pointer", zIndex: 0,
                    transition: "all 0.35s cubic-bezier(0.34,1.56,0.64,1)",
                    animation: `${fadeUp} 0.6s ease-out ${0.1 * idx}s both`,
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: `0 20px 48px ${color}25`,
                      border: `1px solid ${color}40`,
                      zIndex: 2,
                      "& .shape-icon": { animation: `${bounce} 0.6s ease-in-out`, filter: `drop-shadow(0 4px 12px ${color}80)` },
                      "& .shape-num": { color: `${color}18` },
                    },
                  }}>
                    {/* Ghost number */}
                    <Typography className="shape-num" sx={{ position: "absolute", top: 6, right: 14, fontSize: "80px", fontWeight: 900, lineHeight: 1, color: `${color}10`, userSelect: "none", transition: "color 0.3s ease" }}>
                      {String(idx + 1).padStart(2, "0")}
                    </Typography>
                    {/* Step badge with colored dot */}
                    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, px: 1.5, py: 0.4, borderRadius: "100px", background: `${color}12`, border: `1px solid ${color}25`, mb: 2.5, alignSelf: "flex-start" }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: color }} />
                      <Typography sx={{ fontSize: "11px", fontWeight: 700, color: color }}>STEP {String(idx + 1).padStart(2, "0")}</Typography>
                    </Box>
                    <Box className="shape-icon" sx={{ width: 60, height: 60, borderRadius: "16px", background: `${color}12`, border: `1px solid ${color}20`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "30px", mb: 2.5, transition: "all 0.3s ease" }}>
                      {item.icon}
                    </Box>
                    <Typography sx={{ fontSize: { xs: "18px", sm: "20px" }, fontWeight: 700, color: "#1f2937", mb: 1 }}>{item.title}</Typography>
                    <Typography sx={{ fontSize: "14px", color: "#6b7280", lineHeight: 1.7, flex: 1 }}>{item.description}</Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>

      {/* ══════════════════════════════════════════════════════ */}
      {/* CTA */}
      {/* ══════════════════════════════════════════════════════ */}
      <Box sx={{ py: { xs: 10, sm: 12, md: 16 }, background: "linear-gradient(135deg, #3d2fa0 0%, #5b7cfa 50%, #764ba2 100%)", position: "relative", overflow: "hidden" }}>
        {/* Dot grid */}
        <Box sx={{ position: "absolute", inset: 0, zIndex: 0, backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.08) 1px, transparent 1px)", backgroundSize: "38px 38px" }} />
        <Box sx={{ position: "absolute", top: "-20%", right: "-5%", width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 60%)", zIndex: 0 }} />
        <Box sx={{ position: "absolute", bottom: "-20%", left: "-8%", width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(circle, rgba(0,0,0,0.15) 0%, transparent 60%)", zIndex: 0 }} />

        <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{ textAlign: "center" }}>
            {/* Badge */}
            <Box sx={{
              display: "inline-flex", alignItems: "center", gap: 1,
              px: 2.5, py: 1, borderRadius: "100px",
              background: "rgba(255,255,255,0.15)", backdropFilter: "blur(10px)",
              border: "1px solid rgba(255,255,255,0.25)",
              mb: 3, animation: `${fadeUp} 0.6s ease-out both`,
            }}>
              <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: "#fff", animation: `${pulse} 1.5s ease-in-out infinite` }} />
              <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#fff", letterSpacing: "2px" }}>START YOUR TRANSFORMATION</Typography>
            </Box>

            <Typography variant="h3" sx={{ fontSize: { xs: "30px", sm: "40px", md: "50px" }, fontWeight: 800, color: "#fff", mb: 2.5, lineHeight: 1.2, animation: `${fadeUp} 0.8s ease-out 0.1s both` }}>
              We Empower Transformation<br />Through Technology
            </Typography>

            <Typography sx={{ fontSize: { xs: "17px", sm: "19px" }, color: "rgba(255,255,255,0.82)", mb: 5, maxWidth: "620px", mx: "auto", lineHeight: 1.8, animation: `${fadeUp} 0.8s ease-out 0.2s both` }}>
              We expertly oversee every phase of the SDLC, ensuring the delivery of bespoke, high-quality solutions that align with your business objectives.
            </Typography>

            {/* Stat row */}
            <Box sx={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: { xs: 3, sm: 5 }, mb: 5, animation: `${fadeUp} 0.8s ease-out 0.25s both` }}>
              {[
                { val: "200+", label: "Projects Delivered" },
                { val: "98%", label: "Client Retention" },
                { val: "10+", label: "Years of Excellence" },
              ].map((s) => (
                <Box key={s.label} sx={{ textAlign: "center" }}>
                  <Typography sx={{ fontSize: { xs: "30px", sm: "38px" }, fontWeight: 800, color: "#fff", lineHeight: 1 }}>{s.val}</Typography>
                  <Typography sx={{ fontSize: "13px", color: "rgba(255,255,255,0.6)", fontWeight: 600 }}>{s.label}</Typography>
                </Box>
              ))}
            </Box>

            <Button component={Link} to="/contact" variant="contained" sx={{
              backgroundColor: "#fff", color: "#5b7cfa",
              fontWeight: 700, px: 5, py: 1.75, borderRadius: "14px",
              fontSize: "17px", textTransform: "none",
              boxShadow: "0 12px 40px rgba(0,0,0,0.2)",
              animation: `${fadeUp} 0.8s ease-out 0.3s both`,
              transition: "all 0.3s ease",
              "&:hover": { backgroundColor: "#f0f4ff", transform: "translateY(-4px)", boxShadow: "0 16px 48px rgba(0,0,0,0.3)" },
            }}>
              Let's Build Together →
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default About;
